<template>
    <div class="w-full h-full flex flex-col text-left bg-gray-50">
        <form @submit.prevent="submitForm" class="flex-1 flex flex-col overflow-y-auto">
            <div class="p-6 space-y-6">
                <!-- News Basic Info Section -->
                <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center mb-6">
                        <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                            <font-awesome-icon icon="info-circle" class="text-white" />
                        </div>
                        <h3 class="text-lg font-semibold text-gray-900">Basic Information</h3>
                    </div>

                    <div class="space-y-5">
                        <!-- Thumbnail Field - Moved to top -->
                        <div>
                            <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                <font-awesome-icon icon="image" class="mr-2 text-blue-600" />
                                Thumbnail <span class="text-gray-500 ml-1">(Optional)</span>
                            </label>
                            <p class="text-xs text-gray-500 mb-3">Upload an image to represent this news. PNG, JPG,
                                WebP, or GIF files up to 5MB.</p>
                            <div
                                class="rounded-xl border-2 border-dashed border-gray-300 bg-white p-4 md:p-6 text-center">
                                <div v-if="thumbnailPreview" class="space-y-4">
                                    <div class="relative inline-block">
                                        <img :src="thumbnailPreview" alt="News Thumbnail Preview"
                                            class="max-h-56 rounded-lg object-contain border border-gray-200 shadow-sm bg-gray-50" />
                                        <div
                                            class="absolute top-2 right-2 flex flex-col space-y-2 bg-white bg-opacity-90 rounded-lg shadow-md p-2">
                                            <button type="button" @click="triggerThumbnailUpload"
                                                class="px-3 py-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors flex items-center justify-center">
                                                <font-awesome-icon icon="sync" class="mr-1" />
                                                Change
                                            </button>
                                            <button v-if="thumbnailFile" type="button" @click="resetThumbnailSelection"
                                                class="px-3 py-1 text-xs font-semibold text-red-500 hover:text-red-600 transition-colors flex items-center justify-center">
                                                <font-awesome-icon icon="times" class="mr-1" />
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                    <p class="text-sm text-gray-600">
                                        {{ thumbnailFile ? thumbnailFile.name : thumbnailFileDefaultText }}
                                    </p>
                                </div>
                                <div v-else
                                    class="flex flex-col items-center justify-center py-6 text-gray-500 space-y-3">
                                    <div
                                        class="w-16 h-16 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mb-2">
                                        <font-awesome-icon icon="upload" class="text-2xl" />
                                    </div>
                                    <p class="text-sm font-medium text-gray-700">Drag and drop your image here, or</p>
                                    <button type="button" @click="triggerThumbnailUpload"
                                        class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow">
                                        Choose File
                                    </button>
                                    <p class="text-xs text-gray-500">Recommended size: 1200x600px</p>
                                </div>
                                <input ref="thumbnailInput" type="file" class="hidden"
                                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                    @change="onThumbnailSelected" />
                            </div>
                            <p v-if="thumbnailError" class="text-sm text-red-600 mt-2">{{ thumbnailError }}</p>
                        </div>

                        <div>
                            <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                <font-awesome-icon icon="heading" class="mr-2 text-blue-600" />
                                Title <span class="text-red-500 ml-1">*</span>
                            </label>
                            <input v-model="form.title" type="text" required placeholder="Enter news title"
                                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white" />
                        </div>

                        <div>
                            <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                <font-awesome-icon icon="align-left" class="mr-2 text-blue-600" />
                                Description
                            </label>
                            <textarea v-model="form.description" rows="4" placeholder="Enter news description"
                                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white resize-none"></textarea>
                        </div>

                        <div>
                            <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                <font-awesome-icon icon="file-alt" class="mr-2 text-blue-600" />
                                Content
                            </label>
                            <div class="mt-2 quill-container">
                                <QuillEditor ref="quillEditor" v-model:content="form.content" contentType="html"
                                    :options="editorOptions" theme="snow" />
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="calendar-alt" class="mr-2 text-blue-600" />
                                    News Date
                                </label>
                                <date-picker v-model:value="form.news_date" format="DD-MM-YYYY" value-type="format"
                                    type="date" placeholder="Select news date" class="w-full">
                                </date-picker>
                            </div>

                            <div>
                                <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                    <font-awesome-icon icon="building" class="mr-2 text-blue-600" />
                                    Entity
                                </label>
                                <select v-model="form.entity"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white">
                                    <option value="">Select Entity (Optional)</option>
                                    <option value="FCHS">FCHS</option>
                                    <option value="ADPOLY">ADPOLY</option>
                                    <option value="ATHS">ATS</option>
                                    <option value="ADVETI">ADVETI</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end space-x-3">
                <base-button type="button" @click="closeForm" class="bg-gray-200 hover:bg-gray-300 text-gray-700">
                    Cancel
                </base-button>
                <base-button type="submit" :disabled="isSubmitting" class="bg-blue-600 hover:bg-blue-700 text-white">
                    <font-awesome-icon v-if="isSubmitting" icon="spinner" class="animate-spin mr-2" />
                    {{ isSubmitting ? 'Saving...' : (news ? 'Update News' : 'Create News') }}
                </base-button>
            </div>
        </form>
    </div>
</template>

<script>
import gql from "graphql-tag";
import DatePicker from 'vue-datepicker-next';
import 'vue-datepicker-next/index.css';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

const CREATE_NEWS_MUTATION = gql`
    mutation CreateNews($input: NewsInput!) {
        createNews(input: $input) {
            error
            error_code
            message
            data {
                id
                title
                description
                content
                thumbnail
                news_date
                entity
                created_at
                updated_at
            }
        }
    }
`;

const UPDATE_NEWS_MUTATION = gql`
    mutation UpdateNews($id: ID!, $input: NewsInput!) {
        updateNews(id: $id, input: $input) {
            error
            error_code
            message
            data {
                id
                title
                description
                content
                thumbnail
                news_date
                entity
                created_at
                updated_at
            }
        }
    }
`;

export default {
    name: 'NewsFormModal',
    components: {
        DatePicker,
        QuillEditor,
    },
    props: {
        news: {
            type: Object,
            default: null,
        },
        entities: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            form: {
                title: '',
                description: '',
                content: '',
                news_date: null,
                entity: '',
            },
            thumbnailFile: null,
            thumbnailPreview: null,
            thumbnailError: '',
            thumbnailFileDefaultText: '',
            isSubmitting: false,
        };
    },
    watch: {
        news: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.form = {
                        title: newVal.title || '',
                        description: newVal.description || '',
                        content: newVal.content || '',
                        news_date: newVal.news_date ? this.formatDateForPicker(newVal.news_date) : null,
                        entity: newVal.entity || '',
                    };
                    if (newVal.thumbnail) {
                        this.thumbnailPreview = this.resolveThumbnailUrl(newVal.thumbnail);
                        this.thumbnailFileDefaultText = 'Current thumbnail';
                    }
                } else {
                    this.resetForm();
                }
            },
        },
    },
    computed: {
        editorOptions() {
            return {
                modules: {
                    toolbar: {
                        container: [
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'script': 'sub' }, { 'script': 'super' }],
                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                            [{ 'direction': 'rtl' }],
                            [{ 'size': ['small', false, 'large', 'huge'] }],
                            [{ 'color': [] }, { 'background': [] }],
                            [{ 'font': [] }],
                            [{ 'align': [] }],
                            ['link', 'image', 'video'],
                            ['clean'],
                            ['blockquote', 'code-block'],
                        ],
                        handlers: {
                            image: () => this.imageHandler()
                        }
                    }
                },
                placeholder: 'Enter news content...',
                theme: 'snow'
            };
        },
    },
    beforeUnmount() {
        this.revokePreviewUrl(this.thumbnailPreview);
    },
    methods: {
        formatDateForPicker(date) {
            if (!date) return null;
            // If date is already in DD-MM-YYYY format, return it
            if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}$/)) {
                return date;
            }
            // If date is in YYYY-MM-DD format, convert it
            if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const parts = date.split('-');
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            // If it's a Date object
            const d = new Date(date);
            if (isNaN(d.getTime())) return null;
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        },
        formatDateForSubmission(date) {
            if (!date) return null;
            // If date is already in YYYY-MM-DD format, return it
            if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return date;
            }
            // If date is in DD-MM-YYYY format, convert it
            if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}$/)) {
                const parts = date.split('-');
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            // If it's a Date object
            if (date instanceof Date) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return null;
        },
        resetForm() {
            this.form = {
                title: '',
                description: '',
                content: '',
                news_date: null,
                entity: '',
            };
            this.resetThumbnailState();
        },
        resetThumbnailState() {
            this.revokePreviewUrl(this.thumbnailPreview);
            this.thumbnailFile = null;
            this.thumbnailPreview = null;
            this.thumbnailError = '';
            this.thumbnailFileDefaultText = '';
            this.resetThumbnailInput();
        },
        resetThumbnailInput() {
            if (this.$refs.thumbnailInput) {
                this.$refs.thumbnailInput.value = '';
            }
        },
        resolveThumbnailUrl(path) {
            if (!path) {
                return '';
            }
            if (typeof path === 'string' && (path.indexOf('http://') === 0 || path.indexOf('https://') === 0)) {
                return path;
            }
            const baseUrl = process.env.VUE_APP_PUBLIC_URL || '';
            return `${baseUrl}${path}`;
        },
        revokePreviewUrl(url) {
            if (url && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        },
        triggerThumbnailUpload() {
            this.thumbnailError = '';
            this.resetThumbnailInput();
            if (this.$refs.thumbnailInput) {
                this.$refs.thumbnailInput.click();
            }
        },
        onThumbnailSelected(event) {
            const files = event && event.target && event.target.files ? event.target.files : null;
            const file = files && files.length > 0 ? files[0] : null;

            if (!file) {
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                this.thumbnailError = 'Thumbnail must be smaller than 5MB.';
                this.resetThumbnailInput();
                return;
            }

            if (!/image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
                this.thumbnailError = 'Only PNG, JPG, JPEG, GIF or WebP files are allowed.';
                this.resetThumbnailInput();
                return;
            }

            this.thumbnailError = '';
            this.revokePreviewUrl(this.thumbnailPreview);
            this.thumbnailFile = file;
            this.thumbnailPreview = URL.createObjectURL(file);
        },
        resetThumbnailSelection() {
            this.revokePreviewUrl(this.thumbnailPreview);
            this.thumbnailFile = null;
            this.thumbnailPreview = null;
            this.thumbnailFileDefaultText = '';
            this.resetThumbnailInput();
            if (this.news && this.news.thumbnail) {
                this.thumbnailPreview = this.resolveThumbnailUrl(this.news.thumbnail);
                this.thumbnailFileDefaultText = 'Current thumbnail';
            }
        },
        async submitForm() {
            if (!this.form.title || this.form.title.trim() === '') {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Validation Error',
                    text: 'Title is required.',
                });
                return;
            }

            this.isSubmitting = true;
            this.thumbnailError = '';

            try {
                const input = {
                    title: this.form.title.trim(),
                    description: this.form.description ? this.form.description.trim() : null,
                    content: this.form.content ? this.form.content.trim() : null,
                    news_date: this.formatDateForSubmission(this.form.news_date),
                    entity: this.form.entity || '',
                };

                // Add thumbnail file if a new one was selected
                if (this.thumbnailFile) {
                    input.thumbnail = this.thumbnailFile;
                }

                let mutation;
                let variables;

                if (this.news) {
                    // Update existing news
                    mutation = UPDATE_NEWS_MUTATION;
                    variables = {
                        id: this.news.id,
                        input: input,
                    };
                } else {
                    // Create new news
                    mutation = CREATE_NEWS_MUTATION;
                    variables = {
                        input: input,
                    };
                }

                const { data } = await this.$apollo.mutate({
                    mutation: mutation,
                    variables: variables,
                });

                const result = this.news ? data.updateNews : data.createNews;

                if (result.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Failed!',
                        text: result.message || 'Failed to save news',
                    });
                } else {
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: this.news ? 'News updated successfully' : 'News created successfully',
                        showConfirmButton: false,
                        timer: 1500,
                        timerProgressBar: true,
                    });
                    this.$emit('saved');
                }
            } catch (error) {
                console.error('Submit news error:', error);
                let errorMessage = 'Failed to save news';

                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
            } finally {
                this.isSubmitting = false;
            }
        },
        closeForm() {
            this.$emit('close');
        },
        imageHandler() {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/png, image/jpeg, image/jpg, image/gif, image/webp');
            input.click();

            input.onchange = () => {
                const file = input.files[0];
                if (file) {
                    // Check file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'File too large',
                            text: 'Image must be smaller than 5MB.',
                        });
                        return;
                    }

                    // Check file type
                    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Invalid file type',
                            text: 'Only PNG, JPG, JPEG, GIF or WebP images are allowed.',
                        });
                        return;
                    }

                    const reader = new FileReader();
                    reader.onload = (e) => {
                        // Get the Quill instance from the component
                        const quillEditorRef = this.$refs.quillEditor;
                        if (quillEditorRef && quillEditorRef.getQuill) {
                            const quillEditor = quillEditorRef.getQuill();
                            if (quillEditor) {
                                const range = quillEditor.getSelection(true);
                                const index = range ? range.index : 0;
                                quillEditor.insertEmbed(index, 'image', e.target.result);
                                quillEditor.setSelection(index + 1);
                            }
                        }
                    };
                    reader.readAsDataURL(file);
                }
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.quill-container {
    min-height: 400px;

    :deep(.ql-container) {
        min-height: 350px;
        font-size: 14px;
    }

    :deep(.ql-editor) {
        min-height: 350px;
    }

    :deep(.ql-toolbar) {
        border-top-left-radius: 0.5rem;
        border-top-right-radius: 0.5rem;
        border: 1px solid #d1d5db;
        background-color: #f9fafb;
    }

    :deep(.ql-container) {
        border-bottom-left-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
        border: 1px solid #d1d5db;
        border-top: none;
    }

    :deep(.ql-editor.ql-blank::before) {
        font-style: normal;
        color: #9ca3af;
    }

    :deep(.ql-editor img) {
        max-width: 100%;
        height: auto;
        border-radius: 0.375rem;
        margin: 0.5rem 0;
    }
}

:deep(.mx-datepicker) {
    width: 100%;

    .mx-input-wrapper {
        .mx-input {
            width: 100%;
            padding: 0.75rem 1rem;
            border: 1px solid #d1d5db;
            border-radius: 0.5rem;
            font-size: 0.875rem;
            height: 47px;
            box-sizing: border-box;

            &:focus {
                outline: none;
                border-color: #3b82f6;
                box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
            }
        }
    }
}
</style>
